import { TeamAssetSelection, TeamAssetSelectionOutcome } from "common/aiConstants";
import { isOfflinePlayer, isRenderer } from "js/config";

// Selections stay on the slide's metadata, so we'd otherwise re-log them on every data state update
const loggedSelectionIds = new Set<string>();

const OUTCOME_DESCRIPTIONS: Record<TeamAssetSelectionOutcome, string> = {
    "asset-selected": "matched a Team Asset",
    "no-published-assets": "no match - the workspace has no published Team Assets visible to this user",
    "no-tags-on-assets": "no match - none of the published Team Assets have tags",
    "no-relevant-tags": "no match - AI found no tags relevant to the prompt",
    "no-assets-for-selected-tags": "no match - no assets carry the tags AI picked",
    "no-asset-selected": "no match - AI judged none of the tagged assets relevant",
    "unresolved-context": "did not run - could not resolve the workspace or user for the slide",
    "error": "did not finish - the selection threw"
};

function formatAvailableTags(selection: TeamAssetSelection) {
    const tags = selection.availableTags ?? [];
    if (!tags.length) {
        return "(none)";
    }

    return selection.availableTagCount > tags.length
        ? `${tags.join(", ")} (showing ${tags.length} of ${selection.availableTagCount})`
        : tags.join(", ");
}

function logSelection(slideId: string | undefined, selection: TeamAssetSelection) {
    const outcome = OUTCOME_DESCRIPTIONS[selection.outcome] ?? selection.outcome;

    /* eslint-disable no-console */
    console.groupCollapsed(`[team-assets] "${selection.slidePrompt}" - ${outcome}`);
    console.log("Slide:", slideId ?? "(unsaved slide)");
    // Selections persist on the slide, so make it obvious when one is from an older generation
    console.log("Generated:", selection.createdAt ? new Date(selection.createdAt).toLocaleString() : "(unknown)");
    console.log("Prompt:", selection.slidePrompt);
    if (selection.presentationTopic) {
        console.log("Presentation topic:", selection.presentationTopic);
    }
    console.log("Tags picked by AI:", selection.selectedTags?.length ? selection.selectedTags.join(", ") : "(none)");
    console.log("Assets with those tags:", selection.candidateAssetCount);
    console.log("Found an asset:", selection.foundAsset ? "yes" : "no");
    if (selection.foundAsset) {
        // Older records only stored the id, so fall back to that
        console.log("Selected asset:", selection.asset ? {
            id: selection.asset.assetId,
            name: selection.asset.name || "(none)",
            type: selection.asset.type || "(none)",
            alt: selection.asset.alt || "(none)",
            description: selection.asset.description || "(none)",
            tags: selection.asset.tags?.length ? selection.asset.tags.join(", ") : "(none)"
        } : selection.assetId);
    }
    console.log("Tags available to pick from:", formatAvailableTags(selection));
    if (selection.error) {
        console.log("Error:", selection.error);
    }
    console.groupEnd();
    /* eslint-enable no-console */
}

/**
 * Logs Team Asset selections made while generating a slide, so product and QA can see which prompt
 * was used, which tags the AI picked from it and whether an asset came back.
 */
export function logTeamAssetSelections(slideId: string | undefined, metadata: any) {
    // Nobody is watching the console in the renderer or the offline player, and the selection
    // records aren't theirs to see
    if (isRenderer || isOfflinePlayer || (window as any).isPlayer) {
        return;
    }

    const selections = metadata?.teamAssetSelections;
    if (!Array.isArray(selections) || !selections.length) {
        return;
    }

    selections.forEach((selection: TeamAssetSelection) => {
        if (!selection?.id || loggedSelectionIds.has(selection.id)) {
            return;
        }
        loggedSelectionIds.add(selection.id);

        // This runs from the slide model's data state update, so a malformed record must never
        // take slide loading or editing down with it
        try {
            logSelection(slideId, selection);
        } catch (err) {
            /* eslint-disable-next-line no-console */
            console.warn("[team-assets] failed to log a Team Asset selection", err);
        }
    });
}
